import { NavLink, useLocation, useOutlet } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, CalendarDays, GraduationCap, House, RefreshCw, Sprout, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/motion";
import PendingApproval from "@/pages/portal/PendingApproval";
import PushPrompt from "@/components/PushPrompt";

const NAV = [
  ["/portal", "Home", House, "home"],
  ["/portal/renungan", "Renungan", BookOpen, "renungan"],
  ["/portal/grow", "Grow", Sprout, "grow"],
  ["/portal/events", "Events", CalendarDays, "events"],
  ["/portal/profile", "Profile", UserRound, "profile"],
];
const ELEARN = ["/portal/elearning", "E-Learn", GraduationCap, "elearning"];

function BottomNav() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const items = user.elearning_access === "approved" ? [...NAV.slice(0, 4), ELEARN, NAV[4]] : NAV;
  return (
    <nav data-testid="portal-bottom-nav" className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[26rem] z-40 bg-white/90 backdrop-blur-md border border-slate-200 rounded-2xl shadow-lg shadow-slate-900/5 p-1.5">
      <div className={`grid ${items.length === 6 ? "grid-cols-6" : "grid-cols-5"}`}>
        {items.map(([to, label, Icon, id]) => {
          const active = to === "/portal" ? pathname === "/portal" : pathname.startsWith(to);
          return (
            <NavLink key={to} to={to} end={to === "/portal"} data-testid={`nav-${id}`} className={`relative flex flex-col items-center gap-1 py-2 transition-colors ${active ? "text-blue-600" : "text-slate-400 hover:text-slate-600"}`}>
              {active && <motion.span layoutId="nav-pill" transition={{ type: "spring", stiffness: 420, damping: 32 }} className="absolute inset-0.5 rounded-xl bg-blue-50 border border-blue-100" />}
              <motion.span className="relative" animate={active ? { scale: [0.7, 1.15, 1] } : { scale: 1 }} transition={{ duration: 0.45 }}><Icon className="w-5 h-5" /></motion.span>
              <span className={`relative text-[10px] font-bold ${active ? "" : "opacity-70"}`}>{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

function RefreshButton() {
  const [spinning, setSpinning] = useState(false);
  const refresh = () => {
    if (spinning) return;
    setSpinning(true);
    window.dispatchEvent(new CustomEvent("portal:refresh"));
    toast.success("Memperbarui data…");
    setTimeout(() => setSpinning(false), 900);
  };
  return (
    <button type="button" onClick={refresh} data-testid="portal-refresh-button" aria-label="Perbarui"
      className="absolute top-3 right-3 z-[60] w-9 h-9 rounded-full bg-white/80 backdrop-blur-md border border-slate-200 shadow-sm grid place-items-center text-slate-600 hover:text-blue-600 active:scale-90 transition">
      <RefreshCw className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} />
    </button>
  );
}

export default function PortalLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const outlet = useOutlet();

  return (
    <div className="min-h-screen bg-slate-200/60 md:py-6">
      <div data-testid="portal-shell" className="w-full max-w-md mx-auto bg-slate-50 relative min-h-screen md:min-h-[calc(100vh-3rem)] md:shadow-2xl md:rounded-[2rem] overflow-hidden text-slate-800">
        {!(user.nig && user.approval_status === "approved") ? (
          <PendingApproval />
        ) : (
          <>
            <RefreshButton />
            <AnimatePresence mode="wait">
              <motion.main key={location.pathname} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: EASE }} className="pb-32">
                {outlet}
              </motion.main>
            </AnimatePresence>
            <BottomNav />
            <PushPrompt />
          </>
        )}
      </div>
    </div>
  );
}
